'use strict';

const { h, clear } = require('./dom');
const { injectStyles } = require('./theme');

/**
 * Shared scaffold for AepsWidget/DmtWidget/UpiWidget: mounts a card into a
 * container element, manages the header (title/subtitle/back button), swaps
 * screen content with a small fade, and provides loading/error/processing
 * building blocks so each widget only has to describe its own screens.
 */
class BaseWidget {
  constructor(client, opts = {}) {
    this.client = client;
    this.opts = opts;
    this.root = null;
    this.target = null;
    this._overlay = null;
  }

  mount(container) {
    injectStyles();
    const target = typeof container === 'string' ? document.querySelector(container) : container;
    if (!target) throw new Error('BucksBox widget: mount target not found');

    this.target = target;
    this.root = h('div', { class: 'bbx-widget' });
    this.header = h('div', { class: 'bbx-header' });
    this.body = h('div', { class: 'bbx-body' });
    this.root.appendChild(this.header);
    this.root.appendChild(this.body);
    target.appendChild(this.root);
    return this;
  }

  unmount() {
    this.hideOverlay();
    if (this.root && this.root.parentNode) this.root.parentNode.removeChild(this.root);
    this.root = null;
  }

  setHeader({ title, subtitle, showBack, onBack }) {
    clear(this.header);
    if (!title) return;
    if (showBack) {
      this.header.appendChild(h('button', { class: 'bbx-back', onClick: onBack, text: '‹' }));
    }
    this.header.appendChild(
      h('div', { class: 'bbx-header-text' }, [
        h('h1', { text: title }),
        subtitle ? h('p', { text: subtitle }) : null,
      ]),
    );
  }

  showScreen(node) {
    clear(this.body);
    this.body.classList.remove('bbx-body');
    // Force reflow so the fade-in keyframe replays on every screen change.
    void this.body.offsetWidth;
    this.body.classList.add('bbx-body');
    this.body.appendChild(node);
  }

  renderLoading(text) {
    this.showScreen(
      h('div', { class: 'bbx-loading-wrap' }, [
        h('div', { class: 'bbx-spinner' }),
        h('p', { class: 'bbx-muted', text: text || 'Loading…' }),
      ]),
    );
  }

  showOverlay(text) {
    this.hideOverlay();
    this._overlay = h('div', { class: 'bbx-overlay' }, [
      h('div', { class: 'bbx-spinner' }),
      h('p', { text: text || 'Processing…' }),
      h('small', { text: "Please don't close this screen" }),
    ]);
    document.body.appendChild(this._overlay);
  }

  hideOverlay() {
    if (this._overlay && this._overlay.parentNode) this._overlay.parentNode.removeChild(this._overlay);
    this._overlay = null;
  }

  banner(kind, message) {
    return h('div', { class: `bbx-banner bbx-banner-${kind}`, text: message });
  }

  /** Extracts a human message from an SDK error (GatewayClient throws {message, response}). */
  errorMessage(err, fallback) {
    return (err && err.response && err.response.message) || (err && err.message) || fallback || 'Something went wrong';
  }

  fail(err, fallback) {
    const message = this.errorMessage(err, fallback);
    if (typeof this.opts.onError === 'function') this.opts.onError(err);
    return message;
  }

  succeed(result) {
    if (typeof this.opts.onSuccess === 'function') this.opts.onSuccess(result);
  }

  /**
   * Builds a 6-box OTP input. Returns { node, focus, reset, getValue }.
   * `onComplete(otp)` fires once all boxes are filled.
   */
  buildOtpInput({ length = 6, onComplete, seconds = 30, onResend } = {}) {
    const boxes = [];
    for (let i = 0; i < length; i++) {
      boxes.push(
        h('input', {
          type: 'tel',
          maxlength: '1',
          inputmode: 'numeric',
          autocomplete: 'one-time-code',
          onInput: (e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 1);
            e.target.value = val;
            if (val && i < length - 1) boxes[i + 1].focus();
            const otp = boxes.map((b) => b.value).join('');
            if (otp.length === length) onComplete && onComplete(otp);
          },
          onKeydown: (e) => {
            if (e.key === 'Backspace' && !e.target.value && i > 0) boxes[i - 1].focus();
          },
        }),
      );
    }

    const timerText = h('span', { class: 'bbx-muted' });
    const resendLink = h('a', { class: 'bbx-otp-resend', text: 'Resend OTP', style: { display: 'none' } });
    let intervalId = null;

    const startTimer = () => {
      let remaining = seconds;
      resendLink.style.display = 'none';
      timerText.style.display = '';
      const tick = () => {
        timerText.textContent = remaining > 0 ? `Resend OTP in ${remaining}s` : '';
        if (remaining <= 0) {
          clearInterval(intervalId);
          timerText.style.display = 'none';
          resendLink.style.display = '';
        }
        remaining -= 1;
      };
      tick();
      intervalId = setInterval(tick, 1000);
    };

    resendLink.addEventListener('click', () => {
      boxes.forEach((b) => { b.value = ''; });
      boxes[0].focus();
      startTimer();
      onResend && onResend();
    });

    const node = h('div', {}, [
      h('div', { class: 'bbx-otp-boxes' }, boxes),
      h('div', { class: 'bbx-otp-timer' }, [timerText, resendLink]),
    ]);

    startTimer();

    return {
      node,
      focus: () => boxes[0].focus(),
      reset: () => { boxes.forEach((b) => { b.value = ''; }); boxes[0].focus(); },
      getValue: () => boxes.map((b) => b.value).join(''),
      stop: () => clearInterval(intervalId),
    };
  }

  field({ label, type = 'text', placeholder, value = '', hint, onInput, inputmode, maxlength }) {
    const input = h('input', {
      class: 'bbx-input',
      type,
      placeholder,
      value,
      inputmode,
      maxlength,
      onInput: (e) => onInput && onInput(e.target.value),
    });
    return {
      node: h('div', { class: 'bbx-field' }, [
        label ? h('label', { text: label }) : null,
        input,
        hint ? h('p', { class: 'bbx-hint', text: hint }) : null,
      ]),
      input,
    };
  }
}

module.exports = { BaseWidget };
